import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Eye, EyeOff } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { resolveMedia } from "../../siteContext";
import { useAdminT } from "./adminI18n";

const EMPTY = {
  title: "", subtitle: "", date_text: "", image: "", cta_text: "سجّل/سجّلي الآن", cta_url: "", is_active: true,
  title_en: "", subtitle_en: "", date_text_en: "", cta_text_en: "",
};

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm text-luxury-gray mb-2">{label}</label>
    {children}
  </div>
);

export const BannersTab = () => {
  const { t } = useAdminT();
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/banners`, { headers: authHeaders() }).then((res) => setBanners(res.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const startEdit = (b) => {
    setForm({
      title: b.title, subtitle: b.subtitle || "", date_text: b.date_text || "", image: b.image || "",
      cta_text: b.cta_text || "", cta_url: b.cta_url || "", is_active: b.is_active !== false,
      title_en: b.title_en || "", subtitle_en: b.subtitle_en || "", date_text_en: b.date_text_en || "", cta_text_en: b.cta_text_en || "",
    });
    setEditingId(b.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/banners/${editingId}`, form, { headers: authHeaders() });
        toast.success(t("تم تحديث اللافتة"));
      } else {
        await axios.post(`${API}/admin/banners`, form, { headers: authHeaders() });
        toast.success(t("تم نشر اللافتة"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("حذف هذه اللافتة نهائياً؟"))) return;
    await axios.delete(`${API}/admin/banners/${id}`, { headers: authHeaders() });
    toast.success(t("تم الحذف"));
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-banners-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-3xl text-luxury-bone">{t("لافتات الفعاليات القادمة")} ({banners.length})</h2>
        <button
          data-testid="banner-add-button"
          onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t("لافتة جديدة")}
        </button>
      </div>
      <p className="text-luxury-gray text-sm mb-8">{t("اللافتات الظاهرة تُعرض في قسم «الفعاليات القادمة» على الصفحة الرئيسية — للفعاليات والريتريت وورش العمل. إذا تُرك رابط الزر فارغاً يوجّه إلى واتساب برسالة جاهزة باسم الفعالية.")}</p>

      {showForm && (
        <form data-testid="banner-form" onSubmit={save} className="bg-luxury-surface border border-luxury-border p-8 mb-10 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-luxury-gold">{editingId ? t("تعديل اللافتة") : t("لافتة جديدة")}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-luxury-gray hover:text-luxury-bone" aria-label={t("إغلاق")}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("اسم الفعالية *")}><input data-testid="banner-title-input" value={form.title} onChange={set("title")} className={inputCls} required placeholder={t("ريتريت التشافي — بالي")} /></Field>
            <Field label="Title (EN)"><input dir="ltr" value={form.title_en} onChange={set("title_en")} className={inputCls} /></Field>
            <Field label={t("وصف قصير")}><input value={form.subtitle} onChange={set("subtitle")} className={inputCls} placeholder={t("٣ أيام من التشافي العميق")} /></Field>
            <Field label="Subtitle (EN)"><input dir="ltr" value={form.subtitle_en} onChange={set("subtitle_en")} className={inputCls} /></Field>
            <Field label={t("التاريخ / الموعد")}><input data-testid="banner-date-input" value={form.date_text} onChange={set("date_text")} className={inputCls} placeholder={t("١٥ - ١٨ سبتمبر ٢٠٢٦")} /></Field>
            <Field label="Date (EN)"><input dir="ltr" value={form.date_text_en} onChange={set("date_text_en")} className={inputCls} placeholder="Sep 15-18, 2026" /></Field>
            <Field label={t("نص الزر")}><input value={form.cta_text} onChange={set("cta_text")} className={inputCls} /></Field>
            <Field label={t("رابط الزر (اختياري — اتركيه فارغاً لواتساب)")}><input dir="ltr" value={form.cta_url} onChange={set("cta_url")} className={inputCls} placeholder={t("https://... أو /p/event-slug")} /></Field>
          </div>
          <ImageField testid="banner-image-field" label={t("صورة اللافتة")} value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          <label className="flex items-center gap-3 text-luxury-bone text-sm cursor-pointer w-fit">
            <input data-testid="banner-active-toggle" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#D1A05C]" />
            {t("ظاهرة على الصفحة الرئيسية")}
          </label>
          <button data-testid="banner-save-button" type="submit" disabled={saving} className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingId ? t("حفظ التعديلات") : t("نشر اللافتة")}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {banners.map((b, i) => (
          <div key={b.id} data-testid={`banner-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {b.image ? <img src={resolveMedia(b.image)} alt="" className="w-16 h-16 object-cover rounded-lg border border-luxury-border" /> : null}
              <div className="min-w-0">
                <p className="text-luxury-bone font-semibold truncate">{b.title}</p>
                <p className="text-luxury-gray text-xs">{b.date_text || "—"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-3 py-1 rounded-full border flex items-center gap-1.5 ${b.is_active !== false ? "border-luxury-gold text-luxury-gold" : "border-luxury-border text-luxury-gray"}`}>
                {b.is_active !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {b.is_active !== false ? t("ظاهرة") : t("مخفية")}
              </span>
              <button data-testid={`banner-edit-${i}`} onClick={() => startEdit(b)} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors" aria-label={t("تعديل")}>
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button data-testid={`banner-delete-${i}`} onClick={() => remove(b.id)} className="p-2 text-luxury-gray hover:text-red-400 transition-colors" aria-label={t("حذف")}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
        {banners.length === 0 && <p className="text-luxury-gray text-sm border border-luxury-border p-8 text-center">{t("لا توجد لافتات بعد — اضغطي «لافتة جديدة» للإعلان عن فعالية قادمة.")}</p>}
      </div>
    </div>
  );
};
